import ExternalFileUpload from 'external-files/ExternalFileUpload';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {EnrichedFileUploadTask} from 'notebook/notes/drag-and-drop/NoteDropTarget';
import {GridPlaceholderNotePart} from 'notebook/notes/groups/note-part-grid/NotePartGridGroupConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import {FromServerNoteLinkedFile, NotePart, UploadSource} from 'notebook/util/NoteUtilConstants';
import uniqueId from 'util/uniqueId';

import {DEFAULT_GRID_PLACEHOLDER_WIDTH} from './imageTypeUtils';

const {NOTE_LINKED_OBJECT} = NoteConstants;

export function makeGridPlaceholderNotePart(
  taskId: string,
  fileUploadDeferred?: JQueryPromise<FromServerNoteLinkedFile>,
  fileName?: string
): GridPlaceholderNotePart {
  return {
    ...makeDefaultItem(NOTE_LINKED_OBJECT),
    alignment: 'left' as const,
    placeholder: true,
    taskId,
    cid: uniqueId('grid-placeholder-'),
    item: null,
    item_id: null,
    itemWidth: DEFAULT_GRID_PLACEHOLDER_WIDTH,
    _fileUploadDeferred: fileUploadDeferred,
    _fileName: fileName,
  };
}

export interface StandaloneFilePlaceholderDeps {
  fileId: string;
  createFilePlaceholderForTask: (
    task: {
      taskId: string;
      fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>;
    },
    item?: NotePart | null,
    uploadSource?: UploadSource
  ) => NotePart;
}

/**
 * Initiate uploads for the given files and return standalone NoteLinkedFile
 * placeholder NoteParts (one per file). The caller decides how/where to
 * insert them into the note (e.g. replaceItems vs itemInsertHandler).
 */
export function createStandaloneFilePlaceholdersFromFiles(
  files: Array<File>,
  deps: StandaloneFilePlaceholderDeps,
  uploadSource?: UploadSource
): Array<NotePart> {
  if (files.length === 0) {
    return [];
  }
  const tasks = ExternalFileUpload.createTaskAndUploadFiles(deps.fileId, files);
  return createStandaloneFilePlaceholdersFromTasks(tasks, deps, uploadSource);
}

/**
 * Create standalone NoteLinkedFile placeholder NoteParts from
 * already-initiated upload tasks. Used when the uploads were started
 * externally (e.g. by NoteDropTarget).
 */
export function createStandaloneFilePlaceholdersFromTasks(
  tasks: Array<EnrichedFileUploadTask>,
  deps: Pick<StandaloneFilePlaceholderDeps, 'createFilePlaceholderForTask'>,
  uploadSource?: UploadSource
): Array<NotePart> {
  return tasks.map((t) => deps.createFilePlaceholderForTask(t, null, uploadSource));
}
